import React, {Component} from 'react';
import {RouteComponentProps} from 'react-router';
import DashboardLayout, {DashboardTab} from 'layouts/Dashboard';
import {logPageView} from 'services/analytics';
import './index.scss';
import {loadBookings} from "services/bookings";
import {DateTime} from "util/time";
import {Booking, BookingField, BookingsFilters} from "services/entities/booking";
import {Design} from "services/entities/designs";
import {loadDesigns} from "services/designs";
import SpaceSelector from "components/SpaceSelector";
import {DesignIcon, LocationIcon} from "components/Icon";
import SelectField from "components/forms/SelectField";
import {MenuItem} from "@material-ui/core";
import ProgressSpinner from "components/ProgressSpinner";
import {Space, SpaceAnalyticsGroup, SpaceAnalyticsMetric, SpaceAnalyticsValues} from "services/entities/spaces";
import LineChart, {DEFAULT_LINE_COLOR, DISABLED_LINE_COLOR} from "components/charts/LineChart";
import config from "config";
import {formatBookingDate, formatBookingTime} from "util/booking";

interface ViewAnalyticsPageState {
  bookings: Booking[]|null;
  designs: Design[]|null;
  spaces: Space[]|null;
  selectedSpace: string|null;
  selectedDesign: string|null;
}

export default class ViewAnalyticsPage extends Component<RouteComponentProps, ViewAnalyticsPageState> {
  state: ViewAnalyticsPageState = {
    bookings: null,
    designs: null,
    spaces: null,
    selectedSpace: null,
    selectedDesign: null
  };

  componentDidMount(): void {
    logPageView(this.props.location.pathname, 'Analytics');
    this.loadInitialData();
  }

  async loadInitialData() {
    await this.loadBookings();
    this.setState({designs: await loadDesigns()});
  }

  async loadBookings() {
    this.setState({bookings: null});
    const filters: BookingsFilters = {maxTime: DateTime.now()};
    if (this.state.selectedDesign) {
      filters.design = this.state.selectedDesign;
    } else if(this.state.selectedSpace) {
      filters.space = this.state.selectedSpace;
    }
    const bookings = await loadBookings(filters, [BookingField.DailyAnalytics, BookingField.AggregatedAnalytics]);
    this.setState({bookings: bookings});
  }

  static getMaleFemaleRatio(analyticsValues: SpaceAnalyticsValues): string {
    const maleCount = analyticsValues.get(SpaceAnalyticsMetric.MaleCount) || 0;
    const femaleCount = analyticsValues.get(SpaceAnalyticsMetric.FemaleCount) || 0;
    if(maleCount <= 0 && femaleCount <= 0) {
      return '-';
    } else {
      const total = maleCount + femaleCount;
      const malePercentage = Math.round(100 * maleCount / total);
      const femalePercentage = Math.round(100 * femaleCount / total);
      return `${malePercentage}:${femalePercentage}`;
    }
  }

  static getTotalValue(analyticsValues: SpaceAnalyticsValues, metric: SpaceAnalyticsMetric): string {
    if(analyticsValues.has(metric)) {
      return (analyticsValues.get(metric) || 0).toString();
    } else {
      return '-';
    }
  }

  renderPassingChart(values: SpaceAnalyticsGroup[], highlightStart: DateTime, highlightEnd: DateTime) {
    const labels: string[] = [];
    const data: { x: string, y: number|null }[] = [];
    const lineColors: string[] = [];
    const pointColors: string[] = [];
    const day = highlightStart.startOf('day');
    for(let h = config.analytics.minHour; h <= config.analytics.maxHour; h++) {
      const dt = day.addUnits(h, 'hours');
      const label = dt.format("ha");
      labels.push(label);
      data.push({x: label, y: null});
      const isLineHighlighted = dt.lessThan(highlightEnd) && dt.greaterThanOrEqual(highlightStart);
      lineColors.push(isLineHighlighted ? DEFAULT_LINE_COLOR : DISABLED_LINE_COLOR);
      const isPointHighlighted = dt.lessThanOrEqual(highlightEnd) && dt.greaterThanOrEqual(highlightStart);
      pointColors.push(isPointHighlighted ? DEFAULT_LINE_COLOR : DISABLED_LINE_COLOR);
    }
    for(const v of values) {
      const value = v.values.get(SpaceAnalyticsMetric.PassingCount) || 0;
      const time = v.startTime.format("ha");
      const existingIndex = data.findIndex(d => d.x === time);
      if(existingIndex >= 0) {
        data[existingIndex].y = (data[existingIndex].y || 0) + value;
      }
    }
    return <LineChart data={[{ lineColor: lineColors, pointColor: pointColors, data: data }]} xAxis={{ labels: labels }} />;
  }

  renderBooking = (booking: Booking, index: number) => {
    const spaces = this.state.spaces;
    const space: Space|null = spaces ? spaces.find(sp => sp.code === booking.space) || null : null;
    const analyticsValues: SpaceAnalyticsValues = booking.aggregatedAnalytics || new Map<SpaceAnalyticsMetric, number>();
    const dailyAnalytics = booking.dailyAnalytics;
    return <div key={index}>
      <div className="space">
        <span className="label">Where</span>
        <span className="value">{ space?.name }</span>
      </div>
      <div className="time">
        <span className="label">When</span>
        <span className="value">
          <span className="date">{ formatBookingDate(booking) }</span>
          <span className="time">{ formatBookingTime(booking) }</span>
        </span>
      </div>
      <div className="analytics">
        <h3>Report</h3>
        <div className="metrics">
          <div className="passing">
            <span className="label">Passing Traffic</span>
            <span className="value">{ ViewAnalyticsPage.getTotalValue(analyticsValues, SpaceAnalyticsMetric.PassingCount) }</span>
          </div>
          <div className="entrance">
            <span className="label">Entrance Counts</span>
            <span className="value">{ ViewAnalyticsPage.getTotalValue(analyticsValues, SpaceAnalyticsMetric.EntranceCount) }</span>
          </div>
          <div className="gender">
            <span className="label">Male/Female ratio</span>
            <span className="value">{ ViewAnalyticsPage.getMaleFemaleRatio(analyticsValues) }</span>
          </div>
          <div className="age">
            <span className="label">Mean age</span>
            <span className="value">{ Math.round(analyticsValues.get(SpaceAnalyticsMetric.Age) || 0) || '-' }</span>
          </div>
          { dailyAnalytics && <div className="passing-chart">
            { this.renderPassingChart(dailyAnalytics, booking.startTime, booking.endTime) }
          </div> }
        </div>
      </div>
    </div>;
  };

  onSpacesLoaded = (spaces: Space[]) => {
    this.setState({spaces: spaces});
  };

  onSpaceChanged = (space: string) => {
    if(space) {
      this.setState({selectedSpace: space, selectedDesign: null}, () => this.loadBookings());
    } else {
      this.setState({selectedSpace: space}, () => this.loadBookings());
    }
  };

  onDesignChanged = (designCode: string) => {
    if(designCode) {
      const design = this.state.designs ? this.state.designs.find(d => d.code === designCode) || null : null;
      this.setState({
        selectedDesign: designCode,
        selectedSpace: design ? design.space.code : null
      }, () => this.loadBookings());
    } else {
      this.setState({selectedDesign: null}, () => this.loadBookings());
    }
  };

  render() {
    const {bookings, designs, selectedSpace, selectedDesign} = this.state;
    let bookingsClass = 'bookings';
    if(bookings === null) {
      bookingsClass += ' loading';
    } else if(bookings.length === 0) {
      bookingsClass += ' empty';
    }
    const filteredDesigns = designs && selectedSpace ? designs.filter(d => d.space.code === selectedSpace) : designs;
    return <DashboardLayout id="view-analytics-page" currentTab={DashboardTab.Analytics}>
      <h2>Analytics (Beta)</h2>
      <section className="filters">
        <div>
          <label>Location</label>
          <SpaceSelector onChange={this.onSpaceChanged} onSpacesLoaded={this.onSpacesLoaded}
                         emptyItem="All Locations" placeholder="All Locations"
                         value={selectedSpace} startAdornment={<LocationIcon />} />
        </div>
        <div>
          <label>Design</label>
          <SelectField onChange={this.onDesignChanged} placeholder="All Designs"
                        value={selectedDesign} startAdornment={<DesignIcon />}>
            <MenuItem value="">All Designs</MenuItem>
            { filteredDesigns && filteredDesigns.map((d, i) => <MenuItem key={i} value={d.code}>{ d.name }</MenuItem>) }
          </SelectField>
        </div>
      </section>
      <section className={bookingsClass}>
        { bookings === null && <ProgressSpinner /> }
        { bookings !== null && bookings.length > 0 && bookings.map(this.renderBooking) }
        { bookings !== null && bookings.length === 0 && 'No bookings found' }
      </section>
    </DashboardLayout>;
  }
}
